import React, { useState, useEffect } from 'react';
import { X, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// App Store and Play Store URLs
const APP_STORE_URL = 'https://apps.apple.com/es/app/monkeybarber/id6756655968';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=club.monkeybarber.app';

// QR codes that redirect to the respective stores
// Using a dynamic QR service or static QR images
const QR_IOS = '/qr/qr-ios.png';
const QR_ANDROID = '/qr/qr-android.png';

type Platform = 'ios' | 'android' | 'desktop';

const AppDownloadBanner: React.FC = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [platform, setPlatform] = useState<Platform>('desktop');
  const [showQRModal, setShowQRModal] = useState(false);

  useEffect(() => {
    // Check if running inside Capacitor native app - don't show banner
    const isCapacitor = !!(window as any).Capacitor?.isNativePlatform?.();
    if (isCapacitor) return;

    // Don't show on download page (already downloading)
    if (window.location.pathname === '/download' || window.location.pathname === '/app') return;

    // Check if dismissed before (for 7 days)
    const dismissedAt = localStorage.getItem('app-download-dismissed-at');
    if (dismissedAt) {
      const dismissedDate = new Date(dismissedAt);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      if (dismissedDate > sevenDaysAgo) return;
    }

    // Check if dismissed in this session
    const sessionDismissed = sessionStorage.getItem('app-download-session-dismissed');
    if (sessionDismissed === 'true') return;

    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setPlatform('ios');
      // Show banner after a short delay for mobile users
      setTimeout(() => setShowBanner(true), 2000);
    } else if (/android/.test(userAgent)) {
      setPlatform('android');
      // Show banner after a short delay for mobile users
      setTimeout(() => setShowBanner(true), 2000);
    } else {
      setPlatform('desktop');
      // Don't show banner on desktop
    }
  }, []);

  const handleDownload = () => {
    if (platform === 'ios') {
      window.open(APP_STORE_URL, '_blank');
    } else if (platform === 'android') {
      window.open(PLAY_STORE_URL, '_blank');
    }
  };

  const handleShowQR = () => {
    setShowQRModal(true);
  };

  const handleDismiss = (dontShowAgain: boolean = false) => {
    setShowBanner(false);
    setShowQRModal(false);
    if (dontShowAgain) {
      // Dismissal for 7 days
      localStorage.setItem('app-download-dismissed-at', new Date().toISOString());
    } else {
      // Session dismissal
      sessionStorage.setItem('app-download-session-dismissed', 'true');
    }
  };

  const getStoreIcon = () => {
    if (platform === 'ios') {
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
        </svg>
      );
    }
    return (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
      </svg>
    );
  };

  const getStoreName = () => {
    return platform === 'ios' ? 'App Store' : 'Google Play';
  };

  const getQRUrl = () => {
    return platform === 'ios' ? QR_IOS : QR_ANDROID;
  };

  if (!showBanner || platform === 'desktop') return null;

  return (
    <>
      {/* Main Banner - bottom-16 on mobile to stay above bottom nav */}
      <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 z-[101] p-4 shadow-lg border-t border-accent/30/30">
        <div className="max-w-lg mx-auto">
          <div className="flex items-start gap-4">
            {/* App Icon */}
            <div className="flex-shrink-0 w-14 h-14 bg-accent/100 rounded-xl flex items-center justify-center shadow-md">
              <img
                src="/logo/logo_monkey.png"
                alt="MonkeyBarber"
                className="w-10 h-10 object-contain"
              />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-lg">
                {t('appDownload.title', 'Descarga la App')}
              </h3>
              <p className="text-ink-muted text-sm mt-1">
                {t('appDownload.description', 'Mejor experiencia y notificaciones de tu turno')}
              </p>

              {/* Buttons */}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2 bg-accent/100 text-ink rounded-lg font-medium hover:bg-accent/90 transition"
                >
                  {getStoreIcon()}
                  {getStoreName()}
                </button>
                <button
                  onClick={handleShowQR}
                  className="flex items-center gap-2 px-3 py-2 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition"
                >
                  <Smartphone className="w-4 h-4" />
                  QR
                </button>
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={() => handleDismiss(false)}
              className="flex-shrink-0 p-1 text-ink-subtle hover:text-white transition"
              title={t('appDownload.close', 'Cerrar')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRModal && (
        <div
          className="fixed inset-0 z-[102] flex items-center justify-center p-4 bg-black/70"
          onClick={() => setShowQRModal(false)}
        >
          <div
            className="bg-surface rounded-2xl p-6 max-w-sm w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-ink">
                  {t('appDownload.scanQR', 'Escanea el QR')}
                </h3>
                <button
                  onClick={() => setShowQRModal(false)}
                  className="p-1 text-ink-subtle hover:text-ink  transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* QR Code */}
              <div className="bg-white p-4 rounded-xl inline-block">
                <img
                  src={getQRUrl()}
                  alt={`QR ${getStoreName()}`}
                  className="w-48 h-48 object-contain"
                  onError={(e) => {
                    // Fallback to a generated QR placeholder
                    (e.target as HTMLImageElement).src = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(platform === 'ios' ? APP_STORE_URL : PLAY_STORE_URL)}`;
                  }}
                />
              </div>

              {/* Store Badge */}
              <div className="mt-4 flex items-center justify-center gap-2 text-ink-muted">
                {getStoreIcon()}
                <span className="font-medium">{getStoreName()}</span>
              </div>

              {/* Direct Link Button */}
              <button
                onClick={handleDownload}
                className="w-full mt-4 py-3 bg-accent/100 text-ink rounded-xl font-medium hover:bg-accent/90 transition"
              >
                {t('appDownload.openStore', 'Abrir')} {getStoreName()}
              </button>

              {/* Don't show again option */}
              <button
                onClick={() => handleDismiss(true)}
                className="mt-3 text-sm text-ink-subtle hover:text-ink "
              >
                {t('appDownload.dontShowAgain', 'No mostrar de nuevo')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AppDownloadBanner;
